/**
 * SharpMoney.js — Sharp-money tool.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import { fetchDeepLink, openDeepLink } from '../deeplinks';
import { TRACKING_API_URL } from '../config';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline, Box, Stack, Typography, Button, Tooltip, TextField,
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText,
  InputAdornment, IconButton,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import '../App.css';

// ── Theme (EVTool / Arbitrage / Middles) ──

const customTheme = createTheme({
  palette: { primary: { main: '#ff1744', contrastText: '#ffffff' }, background: { default: '#121212', paper: '#1e1e1e' } },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiCheckbox: { styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } } },
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: { styleOverrides: { root: { color: '#ffffff', backgroundColor: '#333333', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' } } } },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: { styleOverrides: { root: { color: '#ffffff', '&.Mui-selected': { backgroundColor: '#444444' }, '&.Mui-selected:hover': { backgroundColor: '#555555' }, '&:hover': { backgroundColor: '#444444' } } } },
    MuiPopover: { styleOverrides: { paper: { backgroundColor: '#1e1e1e', color: '#ffffff', borderRadius: 4, border: '1px solid #444' } } },
  },
});

// ── Constants ──

const SHARP_BOOK_LABELS = { novig: 'Novig', kalshi: 'Kalshi', polymarket: 'Polymarket', prophet_x: 'Prophet X' };
const SHARP_BOOK_IDS = new Set(Object.keys(SHARP_BOOK_LABELS));
const SHARP_BOOK_NAMES = Object.values(SHARP_BOOK_LABELS);



// ── Helpers ──

const renderFilterValue = (sel, opts, label) => {
  if (!sel.length) return label;
  if (sel.length === opts.length) return 'All';
  return sel.length > 1 ? `Multiple selections (${sel.length})` : sel[0];
};

const FilterSelect = ({ id, label, value, options, onChange, disabled }) => (
  <FormControl variant="outlined" size="small" sx={{ minWidth: 140 }}>
    <InputLabel id={id} sx={{ color: '#ffffff' }}>{label}</InputLabel>
    <Select multiple labelId={id} label={label} notched value={value} onChange={onChange}
      renderValue={sel => renderFilterValue(sel, options, label)} disabled={disabled}>
      <MenuItem value="all"><Checkbox checked={value.length === options.length} /><ListItemText primary="Select All" /></MenuItem>
      {options.map(o => <MenuItem key={o} value={o}><Checkbox checked={value.includes(o)} /><ListItemText primary={o} /></MenuItem>)}
    </Select>
  </FormControl>
);

const fmtPrice = (p) => (p == null ? '' : (p > 0 ? `+${p}` : String(p)));
const roiColor = (roi) => { if (roi >= 5) return '#00ff00'; if (roi >= 2) return '#66ff66'; if (roi >= 1) return '#ccffcc'; return '#ffffff'; };

// American odds → implied probability (mirrors Python compute_break_even_win)
const computeBEW = (price) => {
  if (price == null) return null;
  if (price > 0) return 100.0 / (price + 100.0);
  if (price < 0) return Math.abs(price) / (Math.abs(price) + 100.0);
  return null;
};

// Implied probability → American odds (inverse of computeBEW)
const bewToAmerican = (bew) => {
  if (bew == null || bew <= 0 || bew >= 1) return null;
  if (bew > 0.5) return Math.round(-(bew / (1 - bew)) * 100);
  return Math.round(((1 - bew) / bew) * 100);
};

const formatEventTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString([], { month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' }); } catch { return ''; }
};

const prettifyMarket = (m) => {
  if (!m) return '';
  return m.replace(/^1st_/, 'First ').replace(/^2nd_/, 'Second ').replace(/^3rd_/, 'Third ').replace(/^4th_/, 'Fourth ').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const isMoneyline = (market) => { if (!market) return false; const m = market.toLowerCase(); return m === 'moneyline' || m.endsWith('_moneyline'); };

// ── Main Component ──

const SharpMoney = ({ username, isAuthenticated = true }) => {
  const records = useStoreData('sharpMoney') || [];
  const betsData = useStoreData('odds') || [];
  const sportsbooks = useStoreData('sportsbooks') || [];
  const leaguesConfig = useStoreData('leagues') || [];
  const { bookLogos, teamMap } = useLogoMaps();

  const oddsMap = useMemo(() => { const m = new Map(); for (const b of betsData) m.set(b.id, b); return m; }, [betsData]);
  const bookNameMap = useMemo(() => { const m = {}; for (const sb of sportsbooks) m[sb.id] = sb.name || sb.id; return m; }, [sportsbooks]);
  const bookDisplay = useCallback((id) => SHARP_BOOK_LABELS[id] || bookNameMap[id] || id, [bookNameMap]);
  const availableTargetBooks = useMemo(() => {
    if (!sportsbooks || !sportsbooks.length) return [];
    return sportsbooks.filter(sb => !sb.pickem && !SHARP_BOOK_IDS.has(sb.id)).map(sb => sb.name).filter(Boolean).sort();
  }, [sportsbooks]);

  // Derive available leagues from config (matches EVTool/Middles pattern), fallback to data
  const availableLeagues = useMemo(() => {
    if (leaguesConfig.length > 0) {
      return leaguesConfig.map(l => l.name || l.id).filter(Boolean).sort();
    }
    const set = new Set();
    for (const r of records) {
      const bet = oddsMap.get(r.odds_id_1) || oddsMap.get(r.odds_id_2);
      const lg = (bet?.league || '').toUpperCase();
      if (lg) set.add(lg);
    }
    return Array.from(set).sort();
  }, [leaguesConfig, records, oddsMap]);

  const [sharpBookFilter, setSharpBookFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('sharpMoneySharpBooks')) || SHARP_BOOK_NAMES; } catch { return SHARP_BOOK_NAMES; } });
  const [targetBookFilter, setTargetBookFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('sharpMoneyTargetBooks')) || []; } catch { return []; } });
  const [leagueFilter, setLeagueFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('sharpMoneyLeagues')) || []; } catch { return []; } });
  const [minVolumeInput, setMinVolumeInput] = useState(() => localStorage.getItem('sharpMoneyMinVolume') || '500');
  const minVolume = Number(minVolumeInput) || 0;
  const [showBanner, setShowBanner] = useState(() => localStorage.getItem('sharpMoneyBannerDismissed') !== 'true');

  useEffect(() => { localStorage.setItem('sharpMoneySharpBooks', JSON.stringify(sharpBookFilter)); }, [sharpBookFilter]);
  useEffect(() => { localStorage.setItem('sharpMoneyTargetBooks', JSON.stringify(targetBookFilter)); }, [targetBookFilter]);
  useEffect(() => { localStorage.setItem('sharpMoneyLeagues', JSON.stringify(leagueFilter)); }, [leagueFilter]);
  useEffect(() => { localStorage.setItem('sharpMoneyMinVolume', minVolumeInput); }, [minVolumeInput]);

  // One-time initialization: populate empty filters when options first become available.
  // Uses a ref so deselecting all (→ empty array) doesn't re-trigger initialization.
  const filtersInitialized = useRef(false);
  useEffect(() => {
    if (filtersInitialized.current) return;
    if (availableTargetBooks.length > 0 && availableLeagues.length > 0) {
      if (targetBookFilter.length === 0) setTargetBookFilter(availableTargetBooks);
      if (leagueFilter.length === 0) setLeagueFilter(availableLeagues);
      filtersInitialized.current = true;
    }
  }, [availableTargetBooks, availableLeagues, targetBookFilter.length, leagueFilter.length]);

  const handleFilterChange = (event, options, setter) => {
    const val = event.target.value;
    if (val[val.length - 1] === 'all') {
      setter(prev => prev.length === options.length ? [] : options);
    } else {
      setter(val);
    }
  };

  // ── Tracking state ──────────────────────────────────

  const [trackedBets, setTrackedBets] = useState([]);
  const [trackingInProgress, setTrackingInProgress] = useState(new Set());

  useEffect(() => {
    if (!username || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${TRACKING_API_URL}?username=${encodeURIComponent(username)}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setTrackedBets((data.tracked_bets || []).map(b => ({ ...b, id: b.odds_id })));
      } catch (err) { console.error('[TRACKED-BETS] Failed to fetch:', err.message); }
    })();
    return () => { cancelled = true; };
  }, [username, isAuthenticated]);

  const trackedOddsIds = useMemo(() => new Set(trackedBets.map(b => b.odds_id || b.id)), [trackedBets]);

  const apiTrackBet = useCallback(async (row) => {
    const body = {
      username,
      odds_id:         row.betOddsId,
      sportsbook:      row.betBookName,
      sportsbook_logo: row.betBookLogo,
      fixture_id:      row.fixtureId,
      event_name:      `${row.awayAbbr} @ ${row.homeAbbr}`,
      event_start:     row.eventStart,
      league:          row.league,
      player_name:     row.betInfoTop || '',
      player_id:       row.playerId || null,
      market_id:       row.marketId,
      market_display:  row.betInfoBottom || row.betInfoTop || '',
      side:            row.betSide || '',
      points:          row.betPoints,
      price:           row.betPriceRaw,
      roi:             (row.roiPct || 0) / 100,
      home_team:       row.homeAbbr || '',
      away_team:       row.awayAbbr || '',
      home_logo:       row.homeLogo || '',
      away_logo:       row.awayLogo || '',
      source_type:     'Sharp Money',
    };
    const resp = await fetch(TRACKING_API_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok && resp.status !== 409) throw new Error(`Track failed: HTTP ${resp.status}`);
    setTrackedBets(prev => [...prev, { ...body, id: row.betOddsId }]);
  }, [username]);

  const apiUntrackBet = useCallback(async (oddsId) => {
    const resp = await fetch(
      `${TRACKING_API_URL}/${encodeURIComponent(oddsId)}?username=${encodeURIComponent(username)}`,
      { method: 'DELETE' },
    );
    if (!resp.ok && resp.status !== 404) throw new Error(`Untrack failed: HTTP ${resp.status}`);
    setTrackedBets(prev => prev.filter(b => (b.odds_id || b.id) !== oddsId));
  }, [username]);

  const toggleTrack = useCallback(async (row) => {
    if (!isAuthenticated || !username) return;
    const oddsId = row.betOddsId;
    if (!oddsId || trackingInProgress.has(oddsId)) return;
    setTrackingInProgress(prev => new Set(prev).add(oddsId));
    try {
      if (trackedOddsIds.has(oddsId)) {
        await apiUntrackBet(oddsId);
      } else {
        await apiTrackBet(row);
      }
    } catch (err) { console.error('[TRACK] Error:', err.message); }
    finally { setTrackingInProgress(prev => { const s = new Set(prev); s.delete(oddsId); return s; }); }
  }, [isAuthenticated, username, trackedOddsIds, apiTrackBet, apiUntrackBet, trackingInProgress]);

  // ── Enrich, dedup by soft leg, re-aggregate by selected sharp books ──

  const enriched = useMemo(() => {
    if (!records.length) return [];
    const sharpSet = new Set(sharpBookFilter);
    const targetSet = new Set(targetBookFilter);
    const leagueSet = new Set(leagueFilter);

    // Build a name→logo map for sharp books from the quotes
    const sharpBookLogoMap = {};
    for (const [id, name] of Object.entries(SHARP_BOOK_LABELS)) {
      sharpBookLogoMap[id] = bookLogos[name] || '';
    }

    // Step 1: Process all records, apply league + target book filters only
    const dedupMap = {};  // dedupKey → best row so far

    for (const r of records) {
      const ss = r.sharp_slot;
      const betBookId = ss === 1 ? r.sportsbook_id_2 : r.sportsbook_id_1;
      const betBookName = bookDisplay(betBookId);
      if (targetBookFilter.length > 0 && !targetSet.has(betBookName)) continue;

      const bet1 = oddsMap.get(r.odds_id_1); const bet2 = oddsMap.get(r.odds_id_2);
      const sharpBet = ss === 1 ? bet1 : bet2; const betBet = ss === 1 ? bet2 : bet1;
      const refBet = bet1 || bet2;
      const league = (refBet?.league || '').toUpperCase();
      if (leagueFilter.length > 0 && !leagueSet.has(league)) continue;

      const betSideRaw = ss === 1 ? r.side_2 : r.side_1;
      const betPrice = ss === 1 ? r.price_2 : r.price_1;
      const betOddsId = ss === 1 ? r.odds_id_2 : r.odds_id_1;
      const sharpSide = ss === 1 ? r.side_1 : r.side_2;
      const sharpPrice = ss === 1 ? r.price_1 : r.price_2;

      // Dedup key: one row per unique soft-leg opportunity
      const dedupKey = `${r.fixture_id}||${r.market_id}||${r.grouping_key || ''}||${betBookId}||${betSideRaw || ''}`;

      // Keep the row with the strongest arb (lowest bew_sum)
      const existing = dedupMap[dedupKey];
      if (existing && existing._bew_sum <= r.bew_sum) continue;

      const homeAbbr = teamMap[refBet?.homeTeamId]?.abbreviation || teamMap[refBet?.homeTeamId]?.name || '?';
      const awayAbbr = teamMap[refBet?.awayTeamId]?.abbreviation || teamMap[refBet?.awayTeamId]?.name || '?';
      const homeLogo = teamMap[refBet?.homeTeamId]?.logo || '';
      const awayLogo = teamMap[refBet?.awayTeamId]?.logo || '';
      const market = refBet?.market || r.market_id || '';
      const marketDisplay = refBet?.betTypeName || refBet?.marketName || prettifyMarket(market);
      const isMl = isMoneyline(market);

      // Bet Info (matches EVTool playerCellRenderer)
      let betInfoTop, betInfoBottom;
      if (refBet?.marketCategory === 'Player Prop' || refBet?.playerName) {
        betInfoTop = refBet?.playerName || ''; betInfoBottom = marketDisplay;
      } else if (isMl) {
        betInfoTop = sharpBet?.selection || betBet?.selection || ''; betInfoBottom = marketDisplay;
      } else if (market === 'point_spread') {
        betInfoTop = (sharpSide || '').toLowerCase() === 'home'
          ? (teamMap[refBet?.homeTeamId]?.name || sharpBet?.selection || '')
          : (teamMap[refBet?.awayTeamId]?.name || sharpBet?.selection || '');
        betInfoBottom = marketDisplay;
      } else if (market?.includes('total')) {
        betInfoTop = `${awayAbbr}@${homeAbbr}`; betInfoBottom = marketDisplay;
      } else {
        betInfoTop = sharpBet?.selection || refBet?.playerName || marketDisplay;
        betInfoBottom = betInfoTop !== marketDisplay ? marketDisplay : '';
      }

      // Target line parts + Sharp line parts (need both sides' actual points)
      let targetLinePart, targetOddsPart, sharpLinePart;
      const sp = (side, pts) => {
        if (pts == null || pts === '') return '';
        const n = Number(pts); if (Number.isNaN(n)) return '';
        const s = (side || '').toLowerCase();
        const ptsStr = Number.isInteger(n) ? `${n}.0` : String(n);
        if (s === 'over' || s === 'under') return `${s === 'over' ? 'o' : 'u'}${ptsStr}`;
        return n > 0 ? `+${ptsStr}` : ptsStr;
      };
      if (isMl) {
        targetLinePart = ''; targetOddsPart = fmtPrice(betPrice); sharpLinePart = '';
      } else {
        // Use each bet's own points (critical for spreads: home=-1.5, away=+1.5)
        const betPoints = betBet?.points ?? r.points;
        const sharpPoints = sharpBet?.points ?? r.points;
        targetLinePart = sp(betSideRaw, betPoints);
        targetOddsPart = fmtPrice(betPrice);
        sharpLinePart = sp(sharpSide, sharpPoints);
      }

      dedupMap[dedupKey] = {
        id: dedupKey,
        _bew_sum: r.bew_sum,
        _isMl: isMl,
        _betPrice: betPrice,
        league, homeAbbr, awayAbbr, homeLogo, awayLogo,
        eventStart: refBet?.eventStart || null,
        betInfoTop, betInfoBottom,
        betBookName, betBookLogo: bookLogos[betBookName] || '',
        targetLinePart, targetOddsPart, betOddsId, betHasDeeplink: !!betBet?.hasDeeplink,
        _sharpLinePart: sharpLinePart,
        roiPct: r.roi_pct,
        rawQuotes: r.sharp_quotes || [],       // all exchange quotes (unfiltered)
        rawSharpLimit: r.sharp_limit || 0,     // backend aggregate (fallback)
        // Raw fields for bet tracking
        fixtureId: r.fixture_id,
        marketId: r.market_id,
        playerId: refBet?.playerId || refBet?.player_id || null,
        betSide: betSideRaw,
        betPoints: betBet?.points ?? r.points,
        betPriceRaw: betPrice,
      };
    }

    // Step 2: For each deduped row, filter quotes by selected sharp books and re-aggregate
    const out = [];
    for (const row of Object.values(dedupMap)) {
      // Filter quotes to selected sharp books
      const filteredQuotes = row.rawQuotes.filter(q => sharpSet.has(bookDisplay(q.sportsbook_id)));
      const sharpLimit = filteredQuotes.reduce((sum, q) => sum + (q.limit_max || 0), 0)
        || row.rawSharpLimit;

      // Apply min volume filter AFTER re-aggregation
      if (minVolume > 0 && sharpLimit < minVolume) continue;

      // If no quotes pass the sharp book filter, skip entirely
      if (filteredQuotes.length === 0) continue;

      // Hide rows that have already been tracked
      if (trackedOddsIds.has(row.betOddsId)) continue;

      // Build unique sharp book logos from filtered quotes
      const seenBooks = new Set();
      const sharpLogos = [];
      for (const q of filteredQuotes) {
        if (!seenBooks.has(q.sportsbook_id)) {
          seenBooks.add(q.sportsbook_id);
          const name = bookDisplay(q.sportsbook_id);
          sharpLogos.push({ id: q.sportsbook_id, name, logo: sharpBookLogoMap[q.sportsbook_id] || bookLogos[name] || '' });
        }
      }

      // ── Volume-weighted average price + Sharp Δ ──
      const softBew = computeBEW(row._betPrice);
      let roiPct = row.roiPct;
      let weightedSharpPrice = null;

      if (softBew != null && filteredQuotes.length > 0) {
        let weightedBewSum = 0, weightSum = 0;
        for (const q of filteredQuotes) {
          const qBew = computeBEW(q.price);
          const lim = q.limit_max || 0;
          if (qBew != null && lim > 0) {
            weightedBewSum += qBew * lim;
            weightSum += lim;
          }
        }
        if (weightSum > 0) {
          const avgSharpBew = weightedBewSum / weightSum;
          roiPct = Math.max(0, (1 - (avgSharpBew + softBew)) * 100);
          roiPct = Math.round(roiPct * 100) / 100;
          // Convert weighted-avg BEW back to American odds for display
          weightedSharpPrice = bewToAmerican(avgSharpBew);
        }
      }

      // Sharp line display from weighted avg price (or best quote fallback)
      const displayPrice = weightedSharpPrice
        ?? (filteredQuotes.length > 0 ? filteredQuotes.reduce((best, q) => !best || Math.abs(q.price) < Math.abs(best.price) ? q : best, null)?.price : null);

      let sharpLinePart = '', sharpOddsPart = '';
      if (displayPrice != null) {
        if (row._isMl) {
          sharpOddsPart = fmtPrice(displayPrice);
        } else {
          sharpLinePart = row._sharpLinePart || '';
          sharpOddsPart = fmtPrice(displayPrice);
        }
      }

      // Clean up internal fields
      const { rawQuotes, rawSharpLimit, _bew_sum, _isMl, _betPrice, _sharpLinePart: _slp, ...display } = row;
      out.push({
        ...display,
        sharpLimit,
        roiPct,
        sharpLogos,
        sharpLinePart,
        sharpOddsPart,
        sharpQuotes: filteredQuotes.map(q => ({
          ...q,
          bookName: bookDisplay(q.sportsbook_id),
          bookLogo: sharpBookLogoMap[q.sportsbook_id] || bookLogos[bookDisplay(q.sportsbook_id)] || '',
        })),
      });
    }

    out.sort((a, b) => (b.sharpLimit || 0) - (a.sharpLimit || 0));
    return out;
  }, [records, minVolume, sharpBookFilter, targetBookFilter, leagueFilter, trackedOddsIds, oddsMap, teamMap, bookLogos, bookDisplay]);

  // ── Logo cell renderers ──

  const sharpLogoCellRenderer = useCallback((params) => {
    const logos = params.row.sharpLogos || [];
    if (logos.length === 0) return null;

    // 1 logo: exact EVTool logoCellRenderer
    if (logos.length === 1) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: 0 }}>
          <Tooltip title={logos[0].name} placement="top" arrow>
            <img src={logos[0].logo} alt={logos[0].name}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
          </Tooltip>
        </div>
      );
    }

    // 2 logos: side by side, each constrained to half
    if (logos.length === 2) {
      return (
        <div style={{ display: 'flex', width: '100%', height: '100%', padding: 0 }}>
          {logos.map(({ id, name, logo }) => (
            <div key={id} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {logo && <Tooltip title={name} placement="top" arrow>
                <img src={logo} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
              </Tooltip>}
            </div>
          ))}
        </div>
      );
    }

    // 3-4 logos: 2×2 grid, each constrained to quadrant
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', width: '100%', height: '100%', padding: 0, gap: 0 }}>
        {logos.slice(0, 4).map(({ id, name, logo }) => (
          <div key={id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            {logo && <Tooltip title={name} placement="top" arrow>
              <img src={logo} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
            </Tooltip>}
          </div>
        ))}
      </div>
    );
  }, []);

  const betLogoCellRenderer = useCallback((params) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: 0 }}>
      {params.row.betBookLogo && (
        <Tooltip title={params.row.betBookName || ''} placement="top" arrow>
          <img src={params.row.betBookLogo} alt={params.row.betBookName}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
        </Tooltip>
      )}
    </div>
  ), []);

  // ── Detail panel (expandable depth breakdown) ──

  const getDetailPanelContent = useCallback(({ row }) => {
    const quotes = row.sharpQuotes || [];
    if (!quotes.length) return null;
    return (
      <Box sx={{ p: 0, backgroundColor: '#2a2a2a', borderRadius: '4px',
        overflowX: 'auto', overflowY: 'hidden', display: 'flex', flexWrap: 'nowrap', height: 52,
        '&::-webkit-scrollbar': { height: '7px', backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-track': { backgroundColor: '#333333', border: 'none' },
        '&::-webkit-scrollbar-thumb': { backgroundColor: '#888888', border: 'none', '&:hover': { backgroundColor: '#aaaaaa' } },
      }}>
        {quotes.map((q, i) => (
          <Box key={`${q.sportsbook_id}-${q.price}-${i}`} sx={{
            width: 190, flexShrink: 0, flexGrow: 0, display: 'flex', alignItems: 'center',
            borderRight: '1px solid #ffffff', backgroundColor: '#2a2a2a',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 0, width: '100%', height: '100%' }}>
              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-start', height: '100%' }}>
                {q.bookLogo ? (
                  <Tooltip title={q.bookName} placement="top" arrow>
                    <img src={q.bookLogo} alt={q.bookName}
                      style={{ width: 'auto', height: '100%', objectFit: 'contain', display: 'block' }}
                      loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                  </Tooltip>
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', color: '#bbb', px: 0.5 }}>{q.bookName}</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', ml: 0.5 }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#fff', whiteSpace: 'nowrap', lineHeight: '44px', mr: 0.5 }}>{fmtPrice(q.price)}</Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#BBBBBB', whiteSpace: 'nowrap', lineHeight: '44px' }}>${(q.limit_max || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  }, []);

  const getDetailPanelHeight = useCallback(() => 52, []);

  // ── Columns ──

  const columns = useMemo(() => [
    {
      field: 'event', headerName: 'Event', minWidth: 185, flex: 2,
      renderCell: (params) => {
        const r = params.row;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', pl: '8px' }}>
              {r.league && <Typography sx={{ fontSize: '0.875rem' }}>{r.league} | </Typography>}
              {r.awayLogo && <img src={r.awayLogo} alt="" style={{ height: 14, width: 'auto', objectFit: 'contain', paddingRight: 2 }} loading="lazy" />}
              <Typography sx={{ fontSize: '0.875rem' }}>{r.awayAbbr}</Typography>
              <Typography sx={{ fontSize: '0.75rem', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {r.homeLogo && <img src={r.homeLogo} alt="" style={{ height: 14, width: 'auto', objectFit: 'contain', paddingRight: 2 }} loading="lazy" />}
              <Typography sx={{ fontSize: '0.875rem' }}>{r.homeAbbr}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.875rem', pl: '8px', color: '#BBBBBB' }}>{formatEventTime(r.eventStart)}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'betInfo', headerName: 'Bet Info', minWidth: 165, flex: 2,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Tooltip title={params.row.betInfoTop} placement="top" arrow>
            <Typography sx={{ fontSize: '0.875rem', pl: '8px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>{params.row.betInfoTop}</Typography>
          </Tooltip>
          {params.row.betInfoBottom && (
            <Tooltip title={params.row.betInfoBottom} placement="top" arrow>
              <Typography sx={{ fontSize: '0.875rem', pl: '8px', color: '#BBBBBB', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>{params.row.betInfoBottom}</Typography>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'sharpLimit', headerName: 'Sharp Volume', minWidth: 100, flex: 1,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Tooltip title="Sum of selected sharp book (exchange) volume at or above arbitrage level.">
            <InfoIcon sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0.5 }} />
          </Tooltip>
          <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Sharp Volume</Typography>
        </Box>
      ),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: '8px' }}>
          <Typography sx={{ fontSize: '0.875rem', fontVariantNumeric: 'tabular-nums', minWidth: 100, textAlign: 'right' }}>${params.row.sharpLimit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
        </Box>
      ),
    },
    {
      field: 'roiPct', headerName: 'Sharp Δ', minWidth: 80, flex: 1,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Tooltip title="Gap between the volume-weighted sharp line and the target line.">
            <InfoIcon sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0.5 }} />
          </Tooltip>
          <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Sharp Δ</Typography>
        </Box>
      ),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: '8px' }}>
          <Typography sx={{ color: roiColor(params.row.roiPct), fontSize: '0.875rem' }}>
            {params.row.roiPct != null ? `${params.row.roiPct.toFixed(2)}%` : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'sharpLogos', headerName: '', width: 52, minWidth: 52, maxWidth: 52,
      sortable: false, resizable: false, headerAlign: 'center', align: 'center',
      renderCell: sharpLogoCellRenderer,
    },
    {
      field: 'sharpLinePart', headerName: 'Sharp Line', minWidth: 135, flex: 1, sortable: false,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Tooltip title="Volume-weighted average of selected sharp lines at or above arbitrage level.">
            <InfoIcon sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0.5 }} />
          </Tooltip>
          <Typography sx={{ color: '#ffffff', fontWeight: 500, fontSize: '0.875rem' }}>Sharp Line</Typography>
        </Box>
      ),
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: '8px' }}>
          <Typography sx={{ fontSize: '0.875rem', minWidth: 55, textAlign: 'right' }}>{params.row.sharpLinePart}</Typography>
          <Typography sx={{ fontSize: '0.875rem', minWidth: 45, textAlign: 'right', ml: '4px' }}>{params.row.sharpOddsPart}</Typography>
        </Box>
      ),
    },
    {
      field: 'betBookLogo', headerName: '', width: 52, minWidth: 52, maxWidth: 52,
      sortable: false, resizable: false, headerAlign: 'center', align: 'center',
      renderCell: betLogoCellRenderer,
    },
    {
      field: 'targetLinePart', headerName: 'Target Line', minWidth: 135, flex: 1, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pl: '8px' }}>
          <Typography sx={{ fontSize: '0.875rem', minWidth: 55, textAlign: 'right' }}>{params.row.targetLinePart}</Typography>
          <Typography sx={{ fontSize: '0.875rem', minWidth: 45, textAlign: 'right', ml: '4px' }}>{params.row.targetOddsPart}</Typography>
        </Box>
      ),
    },
    {
      field: 'track', headerName: 'Track', sortable: false, width: 65, minWidth: 65, maxWidth: 65, flex: 0,
      headerAlign: 'center', align: 'center',
      renderCell: (params) => {
        const oddsId = params.row.betOddsId;
        const isTracked = trackedOddsIds.has(oddsId);
        const inProgress = trackingInProgress.has(oddsId);
        const disabled = !isAuthenticated || inProgress || !oddsId;
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Checkbox
              checked={isTracked}
              disabled={disabled}
              onChange={() => toggleTrack(params.row)}
              sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' }, '&.Mui-disabled': { color: '#555' }, padding: '4px' }}
            />
          </Box>
        );
      },
    },
    {
      field: 'bet', headerName: '', sortable: false, width: 60, minWidth: 60, maxWidth: 60, flex: 0,
      renderCell: (params) => {
        const disabled = !params.row.betHasDeeplink || !isAuthenticated;
        return (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button onClick={async () => { if (disabled) return; const url = await fetchDeepLink(params.row.betOddsId); if (url) openDeepLink(url); }}
              disabled={disabled} size="small"
              sx={{ color: disabled ? '#666666' : '#00ff00', backgroundColor: 'transparent', border: 'none', padding: '4px 8px', fontSize: '0.875rem', fontWeight: 700, minWidth: 'auto', '&.Mui-disabled': { color: '#666666' } }}>
              BET
            </Button>
          </Box>
        );
      },
    },
  ], [isAuthenticated, sharpLogoCellRenderer, betLogoCellRenderer, trackedOddsIds, trackingInProgress, toggleTrack]);

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: 'calc(100vh - 64px)', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          <Box className="filter-section">
            <Stack direction="row" spacing={2} alignItems="center">
              <FilterSelect id="league-filter" label="League" value={leagueFilter} options={availableLeagues}
                onChange={e => handleFilterChange(e, availableLeagues, setLeagueFilter)} disabled={!isAuthenticated || !availableLeagues.length} />
              <FilterSelect id="sharp-books-filter" label="Sharp Books" value={sharpBookFilter} options={SHARP_BOOK_NAMES}
                onChange={e => handleFilterChange(e, SHARP_BOOK_NAMES, setSharpBookFilter)} disabled={!isAuthenticated} />
              <FilterSelect id="target-books-filter" label="Target Books" value={targetBookFilter} options={availableTargetBooks}
                onChange={e => handleFilterChange(e, availableTargetBooks, setTargetBookFilter)} disabled={!isAuthenticated || !availableTargetBooks.length} />
              <TextField label="Min Volume" variant="outlined" size="small" value={minVolumeInput}
                onChange={e => { const v = e.target.value; if (v === '' || /^\d+$/.test(v)) setMinVolumeInput(v); }}
                onBlur={() => { if (minVolumeInput === '') setMinVolumeInput('0'); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ color: '#ffffff' }}>$</Typography></InputAdornment> }}
                sx={{ width: 130 }} />
            </Stack>
          </Box>

          {showBanner && (
            <Box sx={{
              mx: 2, mb: 1, px: 2, py: 1.5, backgroundColor: '#1a2a3a', borderRadius: '6px',
              border: '1px solid #2a4a6a', display: 'flex', alignItems: 'flex-start', gap: 1.5, flexShrink: 0,
            }}>
              <InfoIcon sx={{ color: '#4a9eff', fontSize: '1.1rem', mt: '2px', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.82rem', color: '#ccc', lineHeight: 1.5, flex: 1 }}>
                <strong style={{ color: '#ffffff' }}>Sharp Money</strong> shows you which side sharp bettors are on and the best available line to bet with them. Pick your trusted exchanges with the "Sharp Books" filter and set a minimum with "Min Volume". Sort by "Sharp Delta" or "Sharp Volume" to find the best ways to back Sharp Money.
              </Typography>
              <IconButton size="small" onClick={() => { setShowBanner(false); localStorage.setItem('sharpMoneyBannerDismissed', 'true'); }}
                sx={{ color: '#888', p: '2px', '&:hover': { color: '#fff' } }}>
                <CloseIcon sx={{ fontSize: '1rem' }} />
              </IconButton>
            </Box>
          )}

          <Box className="table-section" sx={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box className="data-grid-container" sx={{ flex: 1, minHeight: 0 }}>
              <DataGridPro
                rows={enriched} columns={columns} getRowId={r => r.id}
                pageSize={100} rowsPerPageOptions={[50, 100, 250]} pagination
                disableSelectionOnClick disableRowSelectionOnClick disableColumnMenu hideFooterSelectedRowCount
                getDetailPanelContent={getDetailPanelContent}
                getDetailPanelHeight={getDetailPanelHeight}
                initialState={{ sorting: { sortModel: [{ field: 'sharpLimit', sort: 'desc' }] } }}
                sx={{
                  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1e1e1e', borderBottom: '2px solid #444', position: 'sticky', top: '56px', zIndex: 2 },
                  '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                  '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-cell': { color: '#ffffff !important', userSelect: 'none', fontSize: '0.875rem', borderRight: '1px solid rgba(255, 255, 255, 0.3)' },
                  '& .MuiDataGrid-cell[data-field="sharpLogos"]': { padding: 0, borderRight: 'none' },
                  '& .MuiDataGrid-cell[data-field="betBookLogo"]': { padding: 0, borderRight: 'none' },
                  '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                  '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' } },
                  '& .MuiDataGrid-columnSeparator': { visibility: 'visible', color: 'rgba(255, 255, 255, 0.3)' },
                  '& .MuiDataGrid-detailPanelToggleCell': { color: '#ffffff !important' },
                  '& .MuiDataGrid-iconButtonContainer': { color: '#ffffff !important', padding: '0px', backgroundColor: 'transparent !important', '&:hover': { backgroundColor: 'transparent !important' } },
                  '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { color: '#ffffff !important', fontSize: '0.875rem' },
                  '& .MuiSelect-icon': { color: '#ffffff !important' },
                  '& .MuiDataGrid-columnHeaderTitle': { color: '#ffffff !important', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2 },
                  '& .MuiDataGrid-virtualScroller': { overflowY: 'auto !important' },
                  '& .MuiDataGrid-root': { overflowY: 'auto !important', margin: 0 },
                  '& .MuiDataGrid-main': { padding: 0 },
                  '& .MuiDataGrid-footerContainer': { backgroundColor: '#1e1e1e', color: '#ffffff !important', position: 'sticky', bottom: 0, zIndex: 1 },
                }}
              />
            </Box>
          </Box>

        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default SharpMoney;